import { useAuth0 } from "@auth0/auth0-react";
import styles from "./logs.module.css";
import Loader from "../../../components/Loader";
import Footer from "../../../components/Footer";
import { Home } from "react-feather";
import { Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import LogFilter from "./components/LogFilter";
import LogList from "./components/LogList";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { TableLoaderSmall } from "../../../components/LoaderC";
import dayjs from "dayjs";
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

const CHUNK_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// "View analytics" on a campaign card stores the campaign id here so this page opens pre-filtered
const getInitialCampaign = () => {
    try {
        const filtercampaignId = parseInt(localStorage.getItem("emailAnalyticsCampaign"));
        return isNaN(filtercampaignId) ? "" : filtercampaignId;
    } catch (e) {
        return "";
    }
}

const Logs = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [contentLoading, setContentLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(false);
    const [allData, setAllData] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [filterData, setFilterData] = useState(() => ({ searchKey: "", type: "", campaign: getInitialCampaign(), submitted: null }));
    const [campaignList, setCampaignList] = useState([]);

    const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
    const sentinelRef = useRef(null);
    const requestSeq = useRef(0);
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        try {
            localStorage.removeItem("emailAnalyticsCampaign");
        } catch (e) { }
    }, []);

    const fetchLogs = useCallback(async (currentOffset, replace) => {
        const seq = ++requestSeq.current;

        let activeIndicator = "loadingMore";
        if (replace && !hasLoadedOnceRef.current) {
            activeIndicator = "contentLoading";
            setContentLoading(true);
        } else if (replace) {
            activeIndicator = "refetching";
            setRefetching(true);
        } else {
            setLoadingMore(true);
        }
        setLoadMoreError(false);

        let params = { offset: currentOffset, limit: CHUNK_SIZE };
        if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
        if (filterData.type !== "" && filterData.type !== "all") params.type = filterData.type;
        if (filterData.campaign !== "" && filterData.campaign !== "all") params.campaign = filterData.campaign;
        if (filterData.submitted && Array.isArray(filterData.submitted) && filterData.submitted.length === 2) {
            const start = dayjs(filterData.submitted[0]).format("YYYY-MM-DD");
            const end = dayjs(filterData.submitted[1]).format("YYYY-MM-DD");
            params.submitted = `${start},${end}`;
        }

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios.get(process.env.REACT_APP_USER_API_URL + "v1/campaign/email-campaign-logs/paginated", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params,
            });

            // Ignore responses from requests superseded by newer filters
            if (seq !== requestSeq.current) return;

            const newChunk = response.data?.data || [];
            const meta = response.data?.meta || {};
            const nextOffset = meta?.next_offset ?? (currentOffset + newChunk.length);

            setAllData((prev) => (replace ? newChunk : [...prev, ...newChunk]));
            setOffset(nextOffset);
            setHasMore(meta?.has_more ?? (newChunk.length > 0 && nextOffset < (meta?.total ?? 0)));
            setLoadError(false);
            hasLoadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error('[Logs] Failed to load email campaign logs', error);
            toast.error(error?.response?.data?.message || 'Unable to load email analytics. Please try again.');

            if (replace) {
                setLoadError(true);
                setAllData([]);
                setHasMore(false);
            } else {
                setLoadMoreError(true);
            }
        } finally {
            if (seq === requestSeq.current) {
                if (activeIndicator === "contentLoading") setContentLoading(false);
                else if (activeIndicator === "refetching") setRefetching(false);
                else setLoadingMore(false);
            }
        }
    }, [getAccessTokenSilently, debouncedSearch, filterData.type, filterData.campaign, filterData.submitted]);

    // Load the first chunk whenever search or filters change
    useEffect(() => {
        fetchLogs(0, true);
    }, [fetchLogs]);

    // Infinite scroll: load the next chunk when the sentinel enters the viewport
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting) && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
                fetchLogs(offset, false);
            }
        }, { threshold: 0.1 });

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchLogs]);

    useEffect(() => {
        const getCampaignList = async () => {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_USER_API_URL + "v1/campaign/email-templates", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((response) => {
                setCampaignList(response.data.data);
            }).catch(() => { });
        }

        getCampaignList();
    }, [getAccessTokenSilently]);

    return (<>
        <Helmet title="Email analytics" />

        <div className={`${styles.pageTitleWrap}`}>
            <Typography display="inline" className={`${styles.pageTitle}`}>Email Analytics</Typography>
            <nav aria-label="breadcrumb">
                <ol className={`${styles.breadcrumb}`}>
                    <li className={`${styles.breadcrumbItem}`}><span><Home /></span> Email Campaign</li>
                    <li className={`${styles.breadcrumbItem} ${styles.active}`}>Email Analytics</li>
                </ol>
            </nav>
        </div>

        <div className={`${styles.BodyCon}`}>
            <div className={`${styles.mainCardDiv}`}>
                <div className={`${styles.mainCard}`}>
                    {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

                    {!contentLoading && <div className="control-pane">
                        <div className={`${styles.Row}`}>

                            <LogFilter filterData={filterData} setFilterData={setFilterData.bind(this)} campaignList={campaignList} />

                            <div className={refetching ? `${styles.LogMaiSec} ${styles.Refetching}` : `${styles.LogMaiSec}`}>
                                <LogList filteredData={allData} loadError={loadError} onRetry={() => fetchLogs(0, true)} />

                                {/* Sentinel: entering the viewport loads the next chunk */}
                                {allData.length > 0 && <div ref={sentinelRef} className={styles.ScrollSentinel} />}
                            </div>

                            {loadingMore && (
                                <div className={styles.gridScrollStatus}>
                                    <div className={styles.scrollLoaderWrap}>
                                        <div className={styles.scrollSpinner} />
                                        <span>Loading more logs...</span>
                                    </div>
                                </div>
                            )}

                            {loadMoreError && (
                                <div className={styles.gridScrollStatus}>
                                    <p>Couldn't load more logs.</p>
                                    <button type="button" className={styles.RetryBu} onClick={() => fetchLogs(offset, false)}>Retry</button>
                                </div>
                            )}

                        </div>
                    </div>}
                </div>
            </div>
        </div>

        <Footer />
    </>)
}

export default withCustomAuthenticationRequired(Logs, {
    onRedirecting: () => <Loader />,
});